import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UtilityService } from 'src/app/services/utility.service';
import { UserService } from 'src/app/services/user.service';
import { ApplicationService } from '../../services/application.service';
import { RegistrationService } from '../../services/registration.service';
import { CompanyService } from '../../services/company.service';

import { MessageListItem } from '../../models/application';
import { ForgotUserNameInfo } from '../../models/registration';

@Component({
  selector: 'app-forgot-user-name',
  templateUrl: './forgot-user-name.component.html',
  styleUrls: ['./forgot-user-name.component.scss']
})
export class ForgotUserNameComponent implements OnInit {
  private _handleError: HandleError;
  private _logging = environment.logging;
  fieldValidations = environment.fieldValidations;
  fieldValidationsDateOfBirthMinValue = new Date(this.fieldValidations.userRegistration.dateOfBirthMinValue);
  fieldValidationsDateOfBirthMaxValue = new Date(
    this.fieldValidations.userRegistration.dateOfBirthMaxValue !== null
      ? this.fieldValidations.userRegistration.dateOfBirthMaxValue
      : new Date()
  );
  recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showInstructionMessagesSpinner = false;
  instructionMessageList: MessageListItem[] = [];

  forgotUserNameFormPart1: UntypedFormGroup;
  socialSecurityNumberFormControl = new UntypedFormControl();
  lastNameFormControl = new UntypedFormControl();
  dateOfBirthFormControl = new UntypedFormControl();
  recaptchaFormControl = new UntypedFormControl();

  forgotUserNameFormPart2: UntypedFormGroup;
  emailFormControl = new UntypedFormControl();

  forgotUserNameInfo: ForgotUserNameInfo;
  userVerified = false;

  showVerifyUserSpinner = false;
  showVerifyError = false;
  showSubmitSpinner = false;
  showSubmitError = false;
  showSubmitError2 = false;
  showLoginButton = false;
  validacctusser = false;
  registerVisible = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _utilityService: UtilityService,
    private _userService: UserService,
    private _applicationService: ApplicationService,
    private _registrationService: RegistrationService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('ForgotUserNameComponent');
    this.getData();
    this.scrollToTop();
  }

  ngOnInit() {}

  getData(): void {
    const companyCode: string = this._companyService.getCompanyCode();

    this.forgotUserNameInfo = this._registrationService.getNewForgotUserNameInfo();

    // this.getInstructionMessageList(companyCode);

    this.createForm();
  }

  getInstructionMessageList(companyCode: string): void {
    this.showInstructionMessagesSpinner = true;
    this._applicationService.getWelcomeMessageList(companyCode).subscribe(
      resultArray => {
        this.instructionMessageList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('ForgotUserNameComponent.getInstructionMessageList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ForgotUserNameComponent.getInstructionMessageList :: result = ' + JSON.stringify(resultArray));
        }
        this.showInstructionMessagesSpinner = false;
      },
      error => this._handleError('getInstructionMessageList', error)
    );
  }

  getMessageCssClass(item: MessageListItem): string {
    return this._applicationService.getMessageListItemDisplayClass(item);
  }

  createForm(): void {
    this.forgotUserNameFormPart1 = this._formBuilder.group({});
    this.forgotUserNameFormPart1.addControl('socialSecurityNumberFormControl', this.socialSecurityNumberFormControl);
    this.forgotUserNameFormPart1.addControl('lastNameFormControl', this.lastNameFormControl);
    this.forgotUserNameFormPart1.addControl('dateOfBirthFormControl', this.dateOfBirthFormControl);
    this.forgotUserNameFormPart1.addControl('recaptchaFormControl', this.recaptchaFormControl);

    this.forgotUserNameFormPart2 = this._formBuilder.group({});
    this.forgotUserNameFormPart2.addControl('emailFormControl', this.emailFormControl);

    this.setFormValues(null);
    this.setFormValidations(false);
  }

  setFormValues(forgotUserNameInfo: ForgotUserNameInfo = null): void {
    if (forgotUserNameInfo === null) {
      forgotUserNameInfo = this._registrationService.getNewForgotUserNameInfo();
    }

    this.forgotUserNameFormPart1.setValue({
      socialSecurityNumberFormControl: forgotUserNameInfo.ssn,
      lastNameFormControl: forgotUserNameInfo.lastName,
      dateOfBirthFormControl: forgotUserNameInfo.birthDate,
      recaptchaFormControl: null
    });

    this.forgotUserNameFormPart2.setValue({
      emailFormControl: forgotUserNameInfo.homeEmail
    });
  }

  setFormValidations(clearAllValidations: boolean) {
    if (clearAllValidations) {
      this.forgotUserNameFormPart1.get('socialSecurityNumberFormControl').setValidators([]);
      this.forgotUserNameFormPart1.get('lastNameFormControl').setValidators([]);
      this.forgotUserNameFormPart1.get('dateOfBirthFormControl').setValidators([]);
      this.forgotUserNameFormPart1.get('recaptchaFormControl').setValidators([]);

      this.forgotUserNameFormPart2.get('emailFormControl').setValidators([]);
    } else {
      this.forgotUserNameFormPart1
        .get('socialSecurityNumberFormControl')
        .setValidators([
          Validators.required,
          Validators.minLength(this.fieldValidations.userRegistration.socialSecurityNumberMinLength),
          Validators.maxLength(this.fieldValidations.userRegistration.socialSecurityNumberMaxLength)
        ]);
      this.forgotUserNameFormPart1.get('lastNameFormControl').setValidators([Validators.required]);
      this.forgotUserNameFormPart1.get('dateOfBirthFormControl').setValidators([Validators.required]);
      this.forgotUserNameFormPart1.get('recaptchaFormControl').setValidators([Validators.required]);

      this.forgotUserNameFormPart2
        .get('emailFormControl')
        .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.emailAddressPattern)]);
    }

    this.forgotUserNameFormPart1.get('socialSecurityNumberFormControl').markAsPristine();
    this.forgotUserNameFormPart1.get('lastNameFormControl').markAsPristine();
    this.forgotUserNameFormPart1.get('dateOfBirthFormControl').markAsPristine();
    this.forgotUserNameFormPart1.get('recaptchaFormControl').markAsPristine();

    this.forgotUserNameFormPart2.get('emailFormControl').markAsPristine();

    this.forgotUserNameFormPart1.get('socialSecurityNumberFormControl').markAsUntouched();
    this.forgotUserNameFormPart1.get('lastNameFormControl').markAsUntouched();
    this.forgotUserNameFormPart1.get('dateOfBirthFormControl').markAsUntouched();
    this.forgotUserNameFormPart1.get('recaptchaFormControl').markAsUntouched();

    this.forgotUserNameFormPart2.get('emailFormControl').markAsUntouched();

    this.forgotUserNameFormPart1.get('socialSecurityNumberFormControl').updateValueAndValidity();
    this.forgotUserNameFormPart1.get('lastNameFormControl').updateValueAndValidity();
    this.forgotUserNameFormPart1.get('dateOfBirthFormControl').updateValueAndValidity();
    this.forgotUserNameFormPart1.get('recaptchaFormControl').updateValueAndValidity();

    this.forgotUserNameFormPart2.get('emailFormControl').updateValueAndValidity();
  }

  verifyEnabled(): boolean {
    return this.forgotUserNameFormPart1.valid;
  }

  verifyUser() {
    if (this.forgotUserNameFormPart1.valid) {
      this.showVerifyUserSpinner = true;
      this.showVerifyError = false;
      this.validacctusser = true;
      this.registerVisible = false;

      this.forgotUserNameInfo = this._registrationService.getNewForgotUserNameInfo();
      this.forgotUserNameInfo.ssn = this.forgotUserNameFormPart1.get('socialSecurityNumberFormControl').value;
      this.forgotUserNameInfo.lastName = this.forgotUserNameFormPart1.get('lastNameFormControl').value;
      this.forgotUserNameInfo.birthDate = this.forgotUserNameFormPart1.get('dateOfBirthFormControl').value;
      this.forgotUserNameInfo.companyCode = this._companyService.getCompanyCode();

      this._registrationService.getForgotUserNameInfo(this.forgotUserNameInfo).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ForgotUserNameComponent.verifyUser :: result.participantId = ' + result.participantId);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ForgotUserNameComponent.verifyUser :: result = ' + JSON.stringify(result));
          }
          this.showVerifyUserSpinner = false;

          if (result !== null && result.participantId !== null && result.participantId > 0) {
            this.forgotUserNameInfo.participantId = result.participantId;
            this.forgotUserNameInfo.homeEmail = result.homeEmail;
            this.forgotUserNameInfo.homePhone = result.homePhone;
            this.forgotUserNameInfo.cellPhone = result.cellPhone;
            this.forgotUserNameInfo.workPhone = result.workPhone;

            this.forgotUserNameFormPart2.controls['emailFormControl'].setValue(this.forgotUserNameInfo.homeEmail);
            this.forgotUserNameFormPart2.controls['emailFormControl'].markAsDirty();
            this.forgotUserNameFormPart2.controls['emailFormControl'].markAsTouched();
            this.forgotUserNameFormPart2.get('emailFormControl').updateValueAndValidity();

            this.userVerified = true;
            this.showVerifyError = false;
            this.validacctusser = true;
            this.registerVisible = false;
          } else {
            this.userVerified = false;
            this.showVerifyError = true;
            this.validacctusser = false;
            this.registerVisible = false;
          }
        },
        error => {
          this._handleError('verifyUser', error, this.forgotUserNameInfo);
          this.userVerified = false;
          this.showVerifyError = true;
          this.validacctusser = false;
          this.registerVisible = false;
        }
      );
    } else {
      this.userVerified = false;
      this.showVerifyError = true;
      this.validacctusser = false;
      this.registerVisible = false;
    }
  }

  submitEnabled(): boolean {
    return this.forgotUserNameFormPart2.valid;
  }

  registerUser(): void {
    if (this._logging.navigationTraceLog) {
      console.log('ForgotUserNameComponent.registerUser');
    }
    this._router.navigate(['register']);
  }

  resetForm(): void {
    this.userVerified = false;

    this.showVerifyUserSpinner = false;
    this.showVerifyError = false;
    this.showSubmitSpinner = false;
    this.showSubmitError = false;
    this.showSubmitError2 = false;
    this.showLoginButton = false;
    this.validacctusser = false;
    this.registerVisible = false;

    this.forgotUserNameInfo = this._registrationService.getNewForgotUserNameInfo();

    this.setFormValues(null);
    this.setFormValidations(false);

    this.forgotUserNameFormPart1.markAsPristine();
    this.forgotUserNameFormPart1.markAsUntouched();
    this.forgotUserNameFormPart2.markAsPristine();
    this.forgotUserNameFormPart2.markAsUntouched();
  }

  submitUser(): void {
    if (this.forgotUserNameFormPart2.valid) {
      this.showSubmitSpinner = true;

      this._registrationService.sendForgottenUserName(this.forgotUserNameInfo).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ForgotUserNameComponent.submitUser :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ForgotUserNameComponent.submitUser :: result = ' + result);
          }

          this.showSubmitSpinner = false;

          if (Number(result) === 0) {
            this.showSubmitError = false;
            this.showSubmitError2 = false;
            this.showLoginButton = true;
            this.validacctusser = true;
            this.registerVisible = false;
          } else if (Number(result) === -33) {
                    this.showSubmitError = false;
                    this.showSubmitError2 = true;
                    this.showLoginButton = false;
                    this.validacctusser = false;
                    this.registerVisible = true;
            } else {
                this.showSubmitError = true;
                this.showSubmitError2 = false;
                this.showLoginButton = false;
                this.validacctusser = false;
                this.registerVisible = false;
            }
        },
        error => {
          this._handleError('submitUser', error, this.forgotUserNameInfo);
          this.showSubmitSpinner = false;
          this.showSubmitError = true;
          this.showSubmitError2 = false;
          this.showLoginButton = false;
          this.validacctusser = false;
          this.registerVisible = false;
        }
      );
    } else {
      this.showSubmitSpinner = false;
      this.showSubmitError = true;
      this.showSubmitError2 = false;
      this.showLoginButton = false;
      this.validacctusser = false;
      this.registerVisible = false;
    }
  }

  openLogin(): void {
    if (this._logging.navigationTraceLog) {
      console.log('ForgotUserNameComponent.openLogin');
    }
    this._router.navigate(['login']);
  }

  maskSocialSecurityNumber(socialSecurityNumber: string): string {
    return socialSecurityNumber.replace(/\d(?=\d{4})/g, '*');
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }
}
